import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import {
  INewSemenTransactionFormRef,
  NewSemenTransactionForm,
} from '@/components/common/new-semen-transaction-form';
import { NewSemenTransactionFormValues } from '@/components/common/new-semen-transaction-form/schema';
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Semen } from '@/rtk-query.gql';
import { SemenDto } from '@/services/semen-service/types';

type NewSemenTransactionDialogProps = {
  visible: boolean;
  onHide: () => void;
  semenId: string;
  semen?: Semen | SemenDto;
  onSuccess?: (data: NewSemenTransactionFormValues) => void;
  onError?: (error: unknown) => void;
};

export type INewSemenTransactionDialogRef = {
  submit: () => void;
};

export const NewSemenTransactionDialog = forwardRef<
  INewSemenTransactionDialogRef,
  NewSemenTransactionDialogProps
>(({ visible, onHide, semenId, semen, onSuccess, onError }, ref) => {
  const { t } = useTranslation();
  const newFormRef = useRef<INewSemenTransactionFormRef>(null);
  const isBaseUi = useFeatureFlag('new_semen_transaction_prime_to_base_ui');

  useImperativeHandle(ref, () => ({
    submit: () => {
      newFormRef.current?.submit();
    },
  }));

  if (isBaseUi) {
    return (
      <UiDialog
        open={visible}
        onOpenChange={(open) => {
          if (!open) onHide();
        }}
      >
        <DialogContent
          preflight={false}
          className="z-[2000] md:max-w-[1000px]"
          overlayClassName="z-[2000]"
        >
          <DialogHeader className="preflight">
            <DialogTitle>
              {t('semenTransaction.newSemenTransaction')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <NewSemenTransactionForm
              ref={newFormRef}
              semenId={semenId}
              semen={semen}
              onSuccess={onSuccess}
              onError={onError}
              onCancel={onHide}
            />
          </DialogBody>
        </DialogContent>
      </UiDialog>
    );
  }

  return (
    <Dialog
      header="New Semen Transaction"
      visible={visible}
      onHide={onHide}
      modal
      className="w-[1000px] max-w-[95vw]"
    >
      <NewSemenTransactionForm
        ref={newFormRef}
        semenId={semenId}
        semen={semen}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onHide}
      />
    </Dialog>
  );
});
